import { useHistory } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import Tooltip from 'components/Tooltip';
import { VehicleImageCard } from 'components/vehicle-history';
import { useSwipeTabs } from 'hooks/useSwipeTabs';
import PricingBlock from './PricingBlock';
import PaymentHistoryBlock from './PaymentHistoryBlock';
import VehicleTitleBlock from './VehicleTitleBlock';
import { PAYMENT_METHODS_STATUSES } from 'constants/index';
import VinIcon from '../icons/vin.svg';
import MileageIcon from '../icons/mileage.svg';
import SellerIcon from '../icons/seller.svg';
import PickupIcon from '../icons/pickup_instructions.svg';

type QueryEndpoint = 'getAllBuyerTransactions' | 'getEasyPayBuyerTransactions' | 'getCardBuyerTransactions';
type ReduxTab = 'all' | 'easyPay' | 'card';

interface MobileTransactionCardProps {
  vehicle: any;
  queryEndpoint: QueryEndpoint;
  reduxTab: ReduxTab;
}

const TABS = ['Vehicle', 'Payment', 'Title'] as const;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const VehicleTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-top: 10px;
  line-height: 1.3;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PickupInfoRow = styled(InfoRow)`
  align-items: flex-start;
`;

const InfoContent = styled.div`
  display: flex;
`;

const PickupInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const InfoLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #000;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #000;
`;

const ReportOfSaleLink = styled.a`
  font-size: 16px;
  font-weight: 700;
  color: #225a91;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const PickupText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: default;
`;

const VehicleIdBold = styled.span`
  font-weight: 700;
`;

const VehicleIdSlug = styled.span`
  font-weight: 400;
`;

const AwardedLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e0e0e0;
  margin-top: 12px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 0;
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active }) => ($active ? '#005a96' : 'transparent')};
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  color: ${({ $active }) => ($active ? '#005a96' : '#666')};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
`;

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const TabContent = styled.div<{ $direction: 'left' | 'right' | null }>`
  margin-top: 12px;
  ${({ $direction }) =>
    $direction === 'left' &&
    css`
      animation: ${slideInFromRight} 0.25s ease-out;
    `}
  ${({ $direction }) =>
    $direction === 'right' &&
    css`
      animation: ${slideInFromLeft} 0.25s ease-out;
    `}
`;

const EasyPayButton = styled.button`
  width: 100%;
  margin-top: 16px;
  padding: 14px;
  background: #d9d9d9;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  height: 44px;
  color: #000;
  cursor: pointer;
  letter-spacing: 0.5px;
`;

const StatusLabel = styled.div<{ color?: string; textColor?: string }>`
  width: 100%;
  margin-top: 16px;
  padding: 14px;
  background: ${({ color }) => color ?? '#d9d9d9'};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  height: 44px;
  color: ${({ textColor }) => textColor ?? '#000'};
  text-align: center;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileTransactionCard = ({ vehicle, queryEndpoint, reduxTab }: MobileTransactionCardProps) => {
  const history = useHistory();
  const { activeIndex, goToTab, handlers, slideDirection, clearSlideDirection } = useSwipeTabs(TABS.length);

  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const sellerName = vehicle.user?.dealership_name ?? 'N/A';
  const imageUrl = vehicle.preview_image?.url;
  const pickupInstruction = vehicle.user?.seller?.pickup_instruction;
  const auctionId = vehicle.auctionSlug ? `${vehicle.auctionSlug}-${vehicle.orderIndex || '-'}` : null;
  const showAwarded = vehicle.status === 'awarded';

  const renderEasyPayArea = () => {
    const { payment_status, payment_method } = vehicle;

    if (payment_status === PAYMENT_METHODS_STATUSES.APPROVED) {
      return (
        <StatusLabel color="#4caf50" textColor="#fff">
          PAID
        </StatusLabel>
      );
    }

    if (payment_method && payment_status === PAYMENT_METHODS_STATUSES.PENDING) {
      return (
        <StatusLabel color="#CA8D57" textColor="#fff">
          AWAITING ADMIN APPROVAL
        </StatusLabel>
      );
    }

    return <EasyPayButton onClick={() => history.push(`/buyer/bids/${vehicle.id}/easy-pay`)}>EASYPAY</EasyPayButton>;
  };

  const renderTabContent = () => {
    switch (activeIndex) {
      case 0:
        return (
          <>
            <InfoList>
              <InfoRow>
                <VinIcon />
                <InfoContent>
                  <InfoLabel>VIN:&nbsp;</InfoLabel>
                  <InfoValue>{vehicle.vin}</InfoValue>
                </InfoContent>
              </InfoRow>

              <InfoRow>
                <MileageIcon />
                <InfoContent>
                  <InfoLabel>{vehicle.mileage?.toLocaleString()}</InfoLabel>
                  <InfoValue>/Mileage</InfoValue>
                </InfoContent>
              </InfoRow>

              <InfoRow>
                <SellerIcon />
                <InfoContent>
                  <InfoLabel>{sellerName}</InfoLabel>
                </InfoContent>
              </InfoRow>

              <PickupInfoRow>
                <PickupIcon />
                <PickupInfoContent>
                  <InfoContent>
                    <InfoLabel>Pickup instructions</InfoLabel>
                    <div style={{ flex: '1 1 100%' }} />
                    {vehicle.buyer_report_of_sale && (
                      <ReportOfSaleLink href={vehicle.buyer_report_of_sale} target="_blank" rel="noopener noreferrer">
                        Report of Sale
                      </ReportOfSaleLink>
                    )}
                  </InfoContent>
                  {pickupInstruction && (
                    <Tooltip title={pickupInstruction} isTextOverflown placement="bottom" light>
                      <PickupText>{pickupInstruction}</PickupText>
                    </Tooltip>
                  )}
                </PickupInfoContent>
              </PickupInfoRow>
            </InfoList>
          </>
        );
      case 1:
        return (
          <>
            <PricingBlock vehicle={vehicle} />
            <div style={{ height: 24 }} />
            <PaymentHistoryBlock vehicle={vehicle} />
            {renderEasyPayArea()}
          </>
        );
      case 2:
        return <VehicleTitleBlock vehicle={vehicle} queryEndpoint={queryEndpoint} reduxTab={reduxTab} />;
      default:
        return null;
    }
  };

  return (
    <Card>
      <VehicleImageCard
        imageUrl={imageUrl}
        label={
          auctionId && (
            <>
              <VehicleIdBold>Vehicle ID: </VehicleIdBold>
              <VehicleIdSlug>{auctionId}</VehicleIdSlug>
            </>
          )
        }
        onClick={() => history.push(`/vehicles/${vehicle.id}/view`)}
        badge={showAwarded ? <AwardedLabel>Awarded</AwardedLabel> : undefined}
      />

      <CardBody>
        <VehicleTitle>{vehicleTitle}</VehicleTitle>

        <TabBar>
          {TABS.map((tab, i) => (
            <Tab key={tab} $active={activeIndex === i} onClick={() => goToTab(i)}>
              {tab}
            </Tab>
          ))}
        </TabBar>

        <TabContent key={activeIndex} $direction={slideDirection} onAnimationEnd={clearSlideDirection} {...handlers}>
          {renderTabContent()}
        </TabContent>
      </CardBody>
    </Card>
  );
};

export default MobileTransactionCard;
